// Browser "back" spends the window on top, not the screen behind it.
//
// Phone contract (design/MINIMAL-2026-09-18/00-PLAN.md, "Dotyk na telefonie"):
// leaving is deliberate. Every dismissable window owns ONE history entry while it
// is open, so a back gesture spends that entry instead of the route. Nested
// windows stack: one back = one window.
//
// ROUTER NOTE (react-router 7.16, measured in node_modules). main.tsx mounts
// <BrowserRouter>, the component router — NOT createBrowserRouter — so there is no
// data router here and `useBlocker` would throw (it calls useDataRouterContext).
// BrowserRouter subscribes to popstate through its own history object and reads
// `window.history.state` ({ usr, key, idx }) to work out what moved. Our marker
// therefore COPIES that state and only adds a key of its own, and pushes with no
// URL argument: the entry is the same location carrying the same router idx, so
// popping it leaves react-router on the same route with the same key — the close
// is ours alone to handle, and the router never sees a navigation.

import { useCallback, useEffect, useRef } from 'react'

const STATE_KEY = '__dsBack'

export type BackEntry = {
  id: number
  onBack: () => void
  live: boolean
  /** The marker that stood directly under this one when it was armed, if any. */
  under: number | null
}

// One stack for the whole app: last armed is first spent, which is exactly
// "topmost window closes first" for nested windows, and it also orders a leave
// guard armed mid-screen above the windows that were already open.
const stack: BackEntry[] = []
let nextId = 1
// Pops we asked for ourselves. Their popstate must not spend anybody's entry.
let expectedPops = 0
let listening = false
// While a traversal WE started is still in flight, `window.history.state` still
// describes the entry we are leaving — `history.back()` is asynchronous. This is what
// we know will be on top once it lands; `undefined` means nothing is in flight. Two
// windows released in the same tick used to both read that stale state, and the second
// one then believed something had navigated over its marker and abandoned the entry.
let inFlightTop: number | null | undefined
// One-shot, so stepping over a tombstone can never turn into a loop.
let skippingOrphan = false

/** The id of the marker currently on top of the history, `null` if the top is a route. */
function markerOnTop(): number | null {
  if (inFlightTop !== undefined) return inFlightTop
  const id = (window.history.state as Record<string, unknown> | null)?.[STATE_KEY]
  return typeof id === 'number' ? id : null
}

function onPopState(): void {
  if (expectedPops > 0) {
    expectedPops -= 1
    inFlightTop = undefined
    skippingOrphan = false
    return
  }
  // What this back SPENDS is the entry on top of our stack — not whatever state we
  // landed on, which is by definition the entry underneath it.
  const entry = stack.pop()
  if (entry) {
    skippingOrphan = false
    entry.live = false
    entry.onBack()
    return
  }
  // Nothing of ours left to spend, so this is a real route back — and it may have landed
  // ON A TOMBSTONE: a marker nobody owns any more, because its window navigated instead
  // of closing (MergeDialog → the new pack, the "render done" window → Biblioteka,
  // SessionExpired → /login). `disarmBackEntry` could not hand that entry back — going
  // back then would have undone the navigation — so it stayed. Left standing it makes one
  // "back" do NOTHING: same URL, same router key, the screen re-renders itself, which on
  // a phone reads as a jammed back gesture. Step over it right away, so the gesture costs
  // one back and not two. `armBackEntry` always pushes ON TOP of an existing entry, so
  // there is always something below a tombstone: this cannot walk out of the app. The
  // flag makes it one-shot, so a run of tombstones can never become a loop.
  const landed = (window.history.state as Record<string, unknown> | null)?.[STATE_KEY]
  if (!skippingOrphan && typeof landed === 'number') {
    skippingOrphan = true
    expectedPops += 1
    window.history.back()
    return
  }
  skippingOrphan = false
}

/** Take one history entry for `onBack`. Returns the handle to give back later. */
export function armBackEntry(onBack: () => void): BackEntry {
  const state = window.history.state as Record<string, unknown> | null
  const below = state?.[STATE_KEY]
  const entry: BackEntry = {
    id: nextId++,
    onBack,
    live: true,
    under: typeof below === 'number' ? below : null,
  }
  stack.push(entry)
  if (!listening) {
    window.addEventListener('popstate', onPopState)
    listening = true
  }
  window.history.pushState({ ...state, [STATE_KEY]: entry.id }, '')
  inFlightTop = undefined
  return entry
}

/** Give the entry back (window closed by its button, Escape or the scrim). */
export function disarmBackEntry(entry: BackEntry): void {
  const i = stack.indexOf(entry)
  if (i >= 0) stack.splice(i, 1)
  if (!entry.live) return // already spent by a back gesture
  entry.live = false
  // Not on top any more: something navigated over our marker — a link inside the
  // window, or a replace. Going back now would undo THAT navigation, so the entry
  // stays where it is and `onPopState` steps over the tombstone later.
  if (markerOnTop() !== entry.id) return
  inFlightTop = entry.under
  expectedPops += 1
  window.history.back()
}

/**
 * Leave for real, past every window marker still standing.
 *
 * Used by the leave guard once the customer has confirmed. Marking the markers
 * spent BEFORE the traversal is what keeps this honest: history traversal is
 * asynchronous, so a window unmounting right after (the confirmation dialog
 * itself) would otherwise still read its own id in `history.state` and ask for a
 * second back. One `go()` spends them all, and it raises one popstate.
 *
 * @param onLeaveFallback where to go when history has nowhere to go — see below.
 */
export function leaveThroughBackEntries(onLeaveFallback?: () => void): void {
  const live = stack.splice(0).filter((e) => e.live)
  live.forEach((e) => { e.live = false })
  // IS THERE ANYTHING BEHIND THE SCREEN? A traversal past the end of the session
  // history is completely inert — no movement and no popstate (HTML, "traverse the
  // history by a delta"; jsdom implements the same rule). Asking for one blindly is how
  // "Wyjdź" came to do nothing at all on a screen opened as the first entry in a tab (a
  // PWA shortcut, a typed address, a link from another app): the upload kept running AND
  // `expectedPops` stayed raised, so the person's next back gesture was swallowed too.
  //
  // react-router keeps its position in `history.state.idx`, and our markers copy the
  // state of the entry they stand on, so `idx > 0` reads "there is a screen before this
  // one" even with markers on top.
  const idx = (window.history.state as { idx?: number } | null)?.idx
  if (typeof idx === 'number' && idx > 0) {
    inFlightTop = null
    expectedPops += 1
    window.history.go(-(live.length + 1))
    return
  }
  // Nothing behind: history cannot close a tab, so leaving has to be a navigation. The
  // markers we just gave up stay in the history as tombstones, and `onPopState` steps
  // over them. The default destination is the app root, which is where a plain visit
  // lands anyway; a caller that knows better passes its own.
  if (onLeaveFallback) onLeaveFallback()
  else window.location.replace('/')
}

/**
 * Back closes this window. `active` false (a window that cannot be dismissed at
 * all) takes no history entry, so back skips it and reaches the screen.
 *
 * Returns `rearm`: put the entry back after a back gesture that did NOT end in a
 * close. Only the leave guard uses it ("Zostań"); windows ignore it.
 */
export function useBackCloses(active: boolean, onBack: () => void): () => void {
  const cb = useRef(onBack)
  useEffect(() => { cb.current = onBack })

  const entry = useRef<BackEntry | null>(null)
  const release = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wanted = useRef(false)

  const rearm = useCallback(() => {
    if (!wanted.current || entry.current?.live) return
    entry.current = armBackEntry(() => cb.current())
  }, [])

  useEffect(() => {
    if (!active) return
    wanted.current = true
    // StrictMode runs mount → cleanup → mount. The cleanup only SCHEDULES the
    // release, so this second mount cancels it and keeps the entry it already
    // has: no push/back churn against the real session history. The delay pays
    // off on a route change too — by the time it runs, the router has pushed its
    // own entry over ours, which `disarmBackEntry` reads and leaves alone.
    if (release.current) {
      clearTimeout(release.current)
      release.current = null
    } else {
      entry.current = armBackEntry(() => cb.current())
    }
    return () => {
      release.current = setTimeout(() => {
        release.current = null
        wanted.current = false
        const mine = entry.current
        entry.current = null
        if (mine) disarmBackEntry(mine)
      }, 0)
    }
  }, [active])

  return rearm
}

export default useBackCloses
